import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  FileDownload as ExportIcon,
  Delete as DeleteIcon,
  NavigateNext as NextIcon,
  Place as SiteIcon,
} from "@mui/icons-material";
import SiteContact from './EditSiteContact';

const EditSite = ({ siteData, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state (unchanged logic)
  const [formData, setFormData] = useState({
    id: siteData?.id || '',
    araId: siteData?.araId || '',
    company: siteData?.company || '',
    companyId: siteData?.companyId || '',
    recheckCycle: siteData?.recheckCycle || '',
    address: siteData?.address || '',
    address2: siteData?.address2 || '',
    city: siteData?.city || '',
    state: siteData?.state || '',
    zipcode: siteData?.zipcode || '',
    recheckCycle: siteData?.recheckCycle || '',
  });

  const [showNextPage, setShowNextPage] = useState(false);

  const primaryColor = "#0d6efd";

  // Original event handlers (unchanged logic)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleNextClick = () => {
    setShowNextPage(true);
  };

  const handlePreviousClick = () => {
    setShowNextPage(false);
  };

  // Original conditional rendering (unchanged logic)
  if (showNextPage) {
    return <SiteContact onPrevious={handlePreviousClick} siteData={formData} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: primaryColor }}>
            Edit Site
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': {
                backgroundColor: `${primaryColor}10`,
                borderColor: primaryColor,
              }
            }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Site Details Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          bgcolor: primaryColor,
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SiteIcon sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Site Details
            </Typography>
          </Box>
          <IconButton 
            sx={{ color: 'white' }}
            onClick={() => console.log('Delete clicked')}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{
          bgcolor: '#f8f9fa',
          p: { xs: 2, md: 3 }
        }}>
          <form onSubmit={handleSubmit}>
            {/* First Row - ARA ID and Company */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ARA ID"
                  name="araId"
                  value={formData.araId}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Second Row - Address Fields */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address 2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Third Row - City, State, Zipcode, Recheck Cycle */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Zipcode"
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Recheck Cycle"
                  name="recheckCycle"
                  value={formData.recheckCycle}
                  onChange={handleChange}
                  size={isSmall ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: primaryColor + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4
            }}>
              <Button
                type="button"
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNextClick}
                sx={{
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9,
                  },
                  minWidth: 120,
                  px: 3,
                  py: 1.5,
                }}
              >
                Next
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditSite;